import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { Divider, Input, useTheme } from '@mui/material';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import _, { debounce } from 'lodash';
import Countdown from 'react-countdown';
import { CommonErrorHelperTextV1, CommonTextField } from 'gemlux-common-components';
import { theme } from '../../../theme/theme';
import EditSvg from '../../../assets/img/edit-icon.svg';
import WarningSvg from '../../../assets/img/warning-round-icon.svg';
import AlertSvg from '../../../assets/img/alert.svg';
import CustomPhoneNumberInput from '../../V1/CustomPhoneNumberInput/CustomPhoneNumberInput';
import CustomButton from '../../V1/CustomButton/CustomButton';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import { userActions } from '../../../redux/user/userSlice';
import { isPhoneNumberValid, validation } from '../../../schema/validation';
import CustomModal from '../../V1/CustomModal/CustomModal';
import { SquareInputBox } from '../../SquareInputBox/SquareInputBox';
import { OtpVerifyStatus } from '../../../utils/enums/otp-verify-status.enum';
import { authActions } from '../../../redux/auth/authSlice';
import WarningRedSvg from '../../../assets/img/V1/red-warning.svg';
import TickGreenSvg from '../../../assets/img/tick-green.svg';
import useEnv from '../../../utils/use-env-hook';
import CustomLoader from '../../CustomLoader/CustomLoader';
import AvatarEdit from '../../AvatarEdit/AvatarEdit';
import { validateUserData } from '../../../api/userApi';
import CustomTextField from '../../V1/CustomTextField/CustomTextField';

export default function ManageProfile(): ReactElement {
  const dispatch = useAppDispatch();
  const muiTheme = useTheme();
  const { baseUrl } = useEnv();
  const user = useAppSelector((state: RootState) => state.user);
  const [verificationCode, setVerificationCode] = useState<string[]>(Array(4).fill(''));
  const inputRefs = useRef<HTMLInputElement[]>(Array(4).fill(null));
  const componentStatus = useAppSelector((state: RootState) => state.auth.componentStatus);
  const newEmailverifyStatus = _.get(componentStatus, 'newEmailverifyStatus', undefined) as OtpVerifyStatus;
  const error = _.get(componentStatus, 'error', '') as string;
  const countdownStartTime = Number(_.get(componentStatus, 'countdownStartTime', Date.now().toString()));
  const isSubmitting = _.get(componentStatus, 'isSubmitting', false) as boolean;
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [isCheckingEmailUserName, setIsCheckingEmailUserName] = useState(false);
  const [isEmailAlreadyExists, setIsEmailAlreadyExists] = useState(false);
  const [isUsernameAlreadyExists, setIsUsernameAlreadyExists] = useState(false);
  const initialValues = {
    username: user.username,
    firstName: user.firstName,
    lastName: user.familyName,
    email: user.email,
    phone: user.phone,
    file: null,
  };
  const [customPhoneError, setCustomPhoneError] = useState<string | undefined>();

  const validationSchema = yup.object({
    username: validation.username,
    firstName: validation.firstName,
    lastName: validation.familyName,
    email: validation.email,
    phone: yup
      .string()
      .required('Mobile Number is required!')
      .test('phone', 'Invalid Mobile Number!', isPhoneNumberValid),
  });

  const checkEmailOrUserNameTakenDebounce = useCallback(
    debounce(async (input: string, isUsername: boolean) => {
      try {
        setIsCheckingEmailUserName(true);
        if (isUsername) {
          const res = await validateUserData({ username: input });
          setIsUsernameAlreadyExists(res.data);
        } else {
          const res = await validateUserData({ email: input });
          setIsEmailAlreadyExists(res.data);
        }
      } finally {
        setIsCheckingEmailUserName(false);
      }
    }, 1000),
    [],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    onSubmit: (values) => {
      dispatch(userActions.updateUserAsync(values));
    },
  });

  const handlePhoneNumberChange = (pNumber: string) => {
    formik.setFieldValue('phone', pNumber);
  };

  const handleErrorChange = (err: string | undefined) => {
    setCustomPhoneError(err);
  };

  const handleInputImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      formik.setTouched({ ...formik.touched, file: true }, true);
      formik.setFieldValue('file', files[0], true);
    } else {
      formik.setTouched({ ...formik.touched, file: true }, true);
      formik.setFieldValue('file', null, true);
    }
  };

  const isFormChanged = JSON.stringify(formik.values) !== JSON.stringify(initialValues);
  const isEmailChanged = formik.values.email !== initialValues.email;

  useEffect(() => {
    if (user.updateUserStatus) {
      formik.resetForm({
        values: initialValues,
      });
    }
  }, [user.updateUserStatus]);

  useEffect(() => {
    if (formik.values.email && formik.values.email !== formik.initialValues.email && !formik.errors.email) {
      setIsCheckingEmailUserName(true);
      checkEmailOrUserNameTakenDebounce(formik.values.email, false);
    }
    return () => {
      checkEmailOrUserNameTakenDebounce.cancel();
      dispatch(authActions.setComponentStatus({ isEmailAlreadyExists: undefined }));
    };
  }, [formik.values.email, formik.errors.email, checkEmailOrUserNameTakenDebounce]);

  useEffect(() => {
    if (formik.values.username && formik.values.username !== formik.initialValues.username) {
      setIsCheckingEmailUserName(true);
      checkEmailOrUserNameTakenDebounce(formik.values.username, true);
    }
    return () => {
      checkEmailOrUserNameTakenDebounce.cancel();
      dispatch(authActions.setComponentStatus({ isUserNameAlreadyExists: undefined }));
    };
  }, [formik.values.username, formik.errors.username, checkEmailOrUserNameTakenDebounce]);

  useEffect(() => {
    if (newEmailverifyStatus === OtpVerifyStatus.VERIFIED) {
      const timer = setTimeout(() => {}, 1500);
      dispatch(authActions.logOutAsync());
      return () => clearTimeout(timer);
    }
  }, [newEmailverifyStatus]);

  const handleFormSubmit = () => {
    if (!isEmailChanged) {
      formik.handleSubmit();
    } else {
      setIsOtpModalOpen(true);
      setVerificationCode(verificationCode.fill(''));
      dispatch(authActions.setComponentStatus({ error: '', verifyStatus: undefined }));
      dispatch(authActions.resendNewEmailOtpAsync({ email: formik.values.email }));
    }
  };

  const handleVerificationCodeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    if (!Number.isNaN(Number(event.target.value)) && index < 4) {
      const code = [...verificationCode];
      code[index] = event.target.value.slice(-1);
      setVerificationCode(code);

      if (event.target.value !== '' && index < 3) {
        inputRefs.current[index + 1].focus();
      }
      if (_.compact(code).length === 4 && index === 3) {
        dispatch(
          authActions.validateNewEmailOtpAsync({
            code: code.join(''),
            email: formik.values.email,
          }),
        );
      }
    }
  };
  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Backspace' && verificationCode[index] === '' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleResendEmailOtp = () => {
    if (!isSubmitting) {
      setVerificationCode(verificationCode.fill(''));
      dispatch(authActions.setComponentStatus({ error: '', verifyStatus: undefined }));
      dispatch(authActions.resendNewEmailOtpAsync({ email: formik.values.email }));
    }
  };

  const handleCancelChanges = () => {
    setIsOtpModalOpen(false);
    setVerificationCode(verificationCode.fill(''));
    dispatch(authActions.setComponentStatus({ error: '', newEmailverifyStatus: undefined }));
  };

  const getAppearanceImage = (): string | undefined => {
    if (formik.values.file) {
      return URL.createObjectURL(formik.values.file);
    }
    if (user) {
      return `${baseUrl}/${user.avatar}`;
    }
    return undefined;
  };

  const isSubmitDisabled =
    user.updateUserStatus ||
    !isFormChanged ||
    isCheckingEmailUserName ||
    !!customPhoneError ||
    Object.keys(formik.errors).length > 0 ||
    (isEmailAlreadyExists && formik.values.email !== formik.initialValues.email) ||
    (isUsernameAlreadyExists && formik.values.username !== formik.initialValues.username);

  const enterKeyDownHandler = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent unintended behavior

      if (!isSubmitDisabled) {
        handleFormSubmit(); // Only trigger form submission if the button is enabled
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', enterKeyDownHandler);
    return () => {
      document.removeEventListener('keydown', enterKeyDownHandler);
    };
  }, [isSubmitDisabled]);

  const keyMap: number[] = [0, 1, 2, 3];

  if (user.updateUserStatus) {
    return (
      <Box
        width="50%"
        border={`1px solid ${theme.palette.shades.grayBorder}`}
        borderRadius="8px"
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="70%"
      >
        <CustomLoader />
      </Box>
    );
  }

  return (
    <Grid
      container
      spacing={2}
      marginTop={1}
      marginLeft={1}
      width="50%"
      border={`1px solid ${theme.palette.shades.grayBorder}`}
      borderRadius="8px"
    >
      <Grid item xs={12} sm={4} md={3} display="flex" justifyContent="center" alignItems="flex-start">
        <Box sx={{ position: 'relative', width: 96, height: 96, cursor: 'pointer' }}>
          {user.avatar || formik.values.file ? (
            <Avatar
              alt="Profile Image"
              src={getAppearanceImage()}
              sx={{ width: '100%', height: '100%', border: 'none', objectFit: 'cover', cursor: 'pointer' }}
            />
          ) : (
            // TODO: this should be updated
            <AvatarEdit
              editable={false}
              name={user.firstName}
              name2={user.familyName}
              font={theme.typography.h2}
              size="96px"
            />
          )}

          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              cursor: 'pointer',
            }}
            component="img"
            src={EditSvg}
          />
          <Input
            type="file"
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              opacity: 0,
              cursor: 'pointer',
            }}
            inputProps={{
              accept: 'image/*',
            }}
            onChange={handleInputImage}
          />
        </Box>
      </Grid>
      <Grid item xs={12} sm={8} md={9} paddingBottom={1} paddingRight={3}>
        <Box>
          <Grid container spacing={2} marginBottom={2}>
            <Grid item xs={12} sm={12} md={6}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="textH6">Username</Typography>
                <CustomTextField
                  type="text"
                  name="username"
                  placeholder="username"
                  variant="outlined"
                  fullWidth
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formik.touched.username && Boolean(formik.errors.username)) ||
                    (isUsernameAlreadyExists && formik.values.username !== formik.initialValues.username)
                  }
                  helperText={
                    formik.touched.username &&
                    Boolean(formik.errors.username) && (
                      <CommonErrorHelperTextV1 errorText={formik.errors.username ?? ''} />
                    )
                  }
                  FormHelperTextProps={{ style: { ...theme.typography.footnoteRegular2, marginLeft: 0 } }}
                />
                {isUsernameAlreadyExists &&
                  formik.values.username !== formik.initialValues.username &&
                  formik.values.username && (
                    <Box display="flex" flexDirection="row" gap={1}>
                      <Box src={AlertSvg} component="img" />
                      <FormHelperText sx={{ ...theme.typography.footnoteRegular2, color: '#DF0C3D', mt: 0 }}>
                        UserName is already taken!
                      </FormHelperText>
                    </Box>
                  )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="textH6">UserID</Typography>
                <CustomTextField
                  type="text"
                  name="userId"
                  placeholder="UserID"
                  variant="outlined"
                  fullWidth
                  value={user.systemId}
                  disabled
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="textH6">First Name</Typography>
                <CustomTextField
                  type="text"
                  name="firstName"
                  placeholder="John"
                  variant="outlined"
                  fullWidth
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                  helperText={
                    formik.touched.firstName &&
                    Boolean(formik.errors.firstName) && (
                      <CommonErrorHelperTextV1 errorText={formik.errors.firstName ?? ''} />
                    )
                  }
                  FormHelperTextProps={{ style: { ...theme.typography.metaInter, marginLeft: 0 } }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="textH6">Last Name</Typography>
                <CustomTextField
                  type="text"
                  name="lastName"
                  placeholder="Doe"
                  variant="outlined"
                  fullWidth
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                  helperText={
                    formik.touched.lastName &&
                    Boolean(formik.errors.lastName) && (
                      <CommonErrorHelperTextV1 errorText={formik.errors.lastName ?? ''} />
                    )
                  }
                  FormHelperTextProps={{ style: { ...theme.typography.metaInter, marginLeft: 0 } }}
                />
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={6}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="textH6">Email Address</Typography>
                <CustomTextField
                  type="email"
                  name="email"
                  placeholder="john.doe@mail.com"
                  variant="outlined"
                  fullWidth
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formik.touched.email && Boolean(formik.errors.email)) ||
                    (isEmailAlreadyExists && formik.values.email !== formik.initialValues.email)
                  }
                  helperText={
                    formik.touched.email &&
                    Boolean(formik.errors.email) && <CommonErrorHelperTextV1 errorText={formik.errors.email ?? ''} />
                  }
                  FormHelperTextProps={{ style: { ...theme.typography.metaInter, marginLeft: 0 } }}
                />
                {isEmailAlreadyExists && formik.values.email !== formik.initialValues.email && (
                  <Box display="flex" flexDirection="row" gap={1}>
                    <Box src={AlertSvg} component="img" />
                    <FormHelperText sx={{ ...theme.typography.footnoteRegular2, color: '#DF0C3D', mt: 0 }}>
                      Email Address is already taken!
                    </FormHelperText>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="textH6">Mobile</Typography>
                <CustomPhoneNumberInput
                  name="phone"
                  aria-label="phone number"
                  value={formik.values.phone as string}
                  handleChange={handlePhoneNumberChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.phone || customPhoneError ? formik.errors.phone || customPhoneError : undefined}
                  onErrorChange={handleErrorChange}
                />
              </Box>
            </Grid>
            <Divider sx={{ width: '97%', mt: 1.5, ml: 2 }} />
            <Grid item xs={12} sm={12} md={6}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="textH6">Company Name</Typography>
                <CustomTextField
                  type="text"
                  name="companyName"
                  placeholder="Company Name"
                  variant="outlined"
                  fullWidth
                  value={user.companyName}
                  disabled
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12} display="flex" justifyContent="flex-end" padding={3} mt={-2}>
        <Box>
          <CustomButton
            variant="contained"
            color="primary"
            fullWidth
            disabled={isSubmitDisabled}
            loading={user.updateUserStatus}
            onClick={handleFormSubmit}
            sx={{
              '&:hover': {
                backgroundColor: theme.palette.shades.lightBlueColor,
              },
            }}
          >
            {user.updateUserStatus ? 'Updating' : 'Update'}
          </CustomButton>
        </Box>
      </Grid>
      <CustomModal
        modal
        open={isOtpModalOpen}
        title="Verify New Email Address"
        Icon={<Box component="img" src={WarningSvg} />}
        subTitle={`We safeguard your registration and protect your Personal information. 
          Please enter the 4-digit code sent to ${formik.values.email}`}
      >
        <Box display="flex" flexDirection="column" gap="8px" justifyContent="end" alignItems="center" mt={1}>
          <Grid gap={3} display="flex" flexDirection="column">
            <Box display="flex" columnGap={3} justifyContent="center">
              {verificationCode.map((e, index) => (
                <SquareInputBox
                  theme={muiTheme}
                  height="40px"
                  width="40px"
                  autoComplete="off"
                  disabled={
                    verificationCode.filter((code) => code).length === 4 &&
                    newEmailverifyStatus === OtpVerifyStatus.VERIFYING
                  }
                  verifystatus={newEmailverifyStatus}
                  inputRef={(el) => (inputRefs.current[index] = el as HTMLInputElement)}
                  autoFocus={index === 0}
                  onKeyDown={(key) => handleKeyDown(index, key)}
                  color={(() => {
                    if (newEmailverifyStatus === undefined) {
                      return 'primary';
                    }
                    if (newEmailverifyStatus === OtpVerifyStatus.VERIFYING) {
                      return 'success';
                    }
                    return 'primary';
                  })()}
                  key={keyMap[index]}
                  type="text"
                  name={`n${index}`}
                  value={e}
                  onChange={(event) => {
                    handleVerificationCodeChange(event, index);
                  }}
                  variant="outlined"
                />
              ))}
            </Box>
            {isSubmitting && <LinearProgress color="secondary" />}
            {error && (
              <Box display="flex" alignItems="center" justifyContent="center" gap="5px">
                <Box component="img" src={WarningRedSvg} />
                <Typography variant="footnoteRegular2" component="p" textAlign="center" color="error.main">
                  {error}
                </Typography>
              </Box>
            )}

            {newEmailverifyStatus === OtpVerifyStatus.VERIFIED && (
              <Box display="flex" alignItems="center" justifyContent="center" gap="5px">
                <Box component="img" src={TickGreenSvg} />
                <Typography variant="footnoteRegular2" component="p" textAlign="center" color="success.main">
                  Valid PIN!
                </Typography>
              </Box>
            )}
          </Grid>
          {newEmailverifyStatus !== OtpVerifyStatus.VERIFYING && (
            <Box display="flex" justifyContent="center" alignContent="center" mt={1.5} mb={2.5}>
              <Countdown
                key={countdownStartTime}
                zeroPadTime={2}
                date={countdownStartTime + 60 * 1000}
                renderer={({ minutes, seconds, completed }) => {
                  if (completed) {
                    return (
                      <Typography
                        variant="calloutInterRegular"
                        color={theme.palette.secondary.main}
                        sx={{ cursor: 'pointer' }}
                        onClick={handleResendEmailOtp}
                      >
                        Didn’t get the code? Resend
                      </Typography>
                    );
                  }
                  return (
                    <>
                      <Typography
                        variant="calloutInterRegular"
                        component="p"
                        textAlign="center"
                        color="black.contrastText"
                      >
                        Didn’t get the code? Wait&nbsp;
                      </Typography>
                      <Typography
                        variant="calloutInterRegular"
                        component="p"
                        textAlign="start"
                        color="black.contrastText"
                        width="45px"
                      >
                        {_.padStart(minutes.toString(), 2, '0')}:{_.padStart(seconds.toString(), 2, '0')}
                      </Typography>
                    </>
                  );
                }}
              />
            </Box>
          )}
          <CustomButton
            variant="outlined"
            sx={{
              width: '100%',
              color: theme.palette.shades.buttonOutline,
              border: `1px solid ${theme.palette.shades.buttonOutline}`,
              background: 'white',
            }}
            onClick={handleCancelChanges}
          >
            Cancel
          </CustomButton>
        </Box>
      </CustomModal>
    </Grid>
  );
}
